import { useEffect, useMemo, useRef, useState } from "react";
import * as pdfjsLib from "pdfjs-dist";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Checkbox } from "../components/ui/checkbox";
import { CheckCircle2, ChevronLeft, ChevronRight, Download, FileText, LayoutGrid, List, Loader2, LogOut, MessageSquare, Send, ShieldAlert, X } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { CAT_LABEL } from "../components/condivisioni/ShareCreateDialog";
import { modSelect, noShiftTextSelect } from "../lib/mouseSelect";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/public/condivisione`;
const VIEW_KEY = "pgp_share_view";

const ShareMessages = ({ token, data, setData }) => {
  const [txt, setTxt] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const send = async () => {
    if (!txt.trim()) return;
    setBusy(true); setMsg("");
    try {
      const { data: r } = await axios.post(`${API}/${token}/risposta`, { testo: txt.trim(), s: data.session });
      setData((d) => ({ ...d, risposte: [...(d.risposte || []), { testo: txt.trim(), at: r.at, da: "destinatario" }] }));
      setTxt(""); setMsg("Messaggio inviato allo studio.");
    } catch (e) { setMsg(e.response?.data?.detail || "Invio non riuscito, riprova."); }
    finally { setBusy(false); }
  };
  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5 space-y-4" data-testid="share-pub-messages">
      {data.note && (
        <div data-testid="share-pub-note">
          <div className="text-xs uppercase text-slate-500 mb-1">Note dello studio</div>
          <div className="rounded-md border-l-4 border-amber-400 bg-amber-50 px-3 py-2 text-sm text-slate-800 whitespace-pre-wrap break-words">{data.note}</div>
        </div>
      )}
      {(data.risposte || []).length > 0 && (
        <div className="space-y-2" data-testid="share-pub-replies">
          <div className="text-xs uppercase text-slate-500">Conversazione con lo studio</div>
          {data.risposte.map((r, i) => {
            const st = r.da === "studio";
            return (
              <div key={i} className={`flex ${st ? "justify-start" : "justify-end"}`}>
                <div className={`max-w-[85%] rounded-md border px-3 py-2 text-sm ${st ? "bg-amber-50 border-amber-200" : "bg-slate-50 border-slate-200"}`} data-testid={`share-pub-reply-${i}`}>
                  <div className="text-[11px] text-slate-500"><strong className={st ? "text-amber-700" : "text-slate-600"}>{st ? "Studio" : "Tu"}</strong>{r.at ? ` · ${new Date(r.at).toLocaleString("it-IT")}` : ""}</div>
                  <div className="whitespace-pre-wrap break-words text-slate-800">{r.testo}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <div>
        <div className="text-xs uppercase text-slate-500 mb-1 flex items-center gap-1.5"><MessageSquare className="h-3.5 w-3.5" /> Rispondi allo studio</div>
        <textarea value={txt} onChange={(e) => setTxt(e.target.value)} rows={3} maxLength={5000} placeholder="Scrivi qui il tuo messaggio per lo studio" data-testid="share-pub-reply-text"
          className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-1 focus:ring-amber-500" />
        <div className="mt-2 flex items-center gap-3">
          <button type="button" onClick={send} disabled={busy || !txt.trim()} data-testid="share-pub-reply-send"
            className="inline-flex items-center gap-1.5 rounded-md bg-amber-500 hover:bg-amber-600 disabled:opacity-50 px-4 py-2 text-sm font-semibold text-slate-900 transition-colors">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Invia allo studio
          </button>
          {msg && <span className="text-sm text-slate-600" data-testid="share-pub-reply-msg">{msg}</span>}
        </div>
      </div>
    </section>
  );
};

pdfjsLib.GlobalWorkerOptions.workerSrc = "https://unpkg.com/pdfjs-dist@4.0.379/build/pdf.worker.min.mjs";
const isImg = (a) => /^image\//i.test(a.content_type || "") || /\.(jpe?g|png|gif|webp|bmp|heic|heif)$/i.test(a.nome || "");
const isPdf = (a) => /pdf/i.test(a.content_type || "") || /\.pdf$/i.test(a.nome || "");
const pdfCache = new Map();
let pdfActive = 0;
const pdfQueue = [];
const pdfNext = () => { while (pdfActive < 2 && pdfQueue.length) { const job = pdfQueue.shift(); pdfActive++; job().finally(() => { pdfActive--; pdfNext(); }); } };
async function renderPdf(src) {
  const buf = (await axios.get(src, { responseType: "arraybuffer" })).data;
  const pdf = await pdfjsLib.getDocument({ data: buf }).promise;
  try {
    const page = await pdf.getPage(1);
    const v0 = page.getViewport({ scale: 1 });
    const vp = page.getViewport({ scale: 360 / Math.max(v0.width, v0.height) });
    const c = document.createElement("canvas");
    c.width = Math.ceil(vp.width); c.height = Math.ceil(vp.height);
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, c.width, c.height);
    await page.render({ canvasContext: ctx, viewport: vp }).promise;
    return c.toDataURL("image/jpeg", 0.75);
  } finally { pdf.destroy(); }
}
const pdfThumb = (id, src) => {
  if (!pdfCache.has(id)) pdfCache.set(id, new Promise((resolve) => { pdfQueue.push(() => renderPdf(src).then(resolve, () => resolve(null))); pdfNext(); }));
  return pdfCache.get(id);
};

const Thumb = ({ base, a, big, s, src }) => {
  const [url, setUrl] = useState(null);
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || visible) return;
    const io = new IntersectionObserver((es) => { if (es.some((e) => e.isIntersecting)) { setVisible(true); io.disconnect(); } });
    io.observe(el);
    return () => io.disconnect();
  }, [visible]);
  useEffect(() => {
    let on = true;
    if (!visible || !(isImg(a) || isPdf(a))) return;
    axios.get(`${base}/files/${a.id}/thumb`, { params: { s } }).then(({ data }) => data.url).catch(() => null).then((u) => {
      if (!on) return;
      if (u) setUrl(u);
      else if (isImg(a)) setUrl(src);
      else pdfThumb(a.id, src).then((d) => on && d && setUrl(d));
    });
    return () => { on = false; };
  }, [base, a, s, src, visible]);
  const cls = big ? "h-36 w-full" : "h-10 w-10";
  return url ? <img src={url} alt="" onError={() => setUrl(null)} data-testid={`share-pub-thumb-${a.id}`} className={`${cls} ${isPdf(a) ? "object-contain bg-white" : "object-cover"} rounded`} />
    : <div ref={ref} className={`${cls} rounded bg-slate-100 flex items-center justify-center`}><FileText className={big ? "h-10 w-10 text-slate-400" : "h-5 w-5 text-slate-400"} /></div>;
};

const PdfPages = ({ src }) => {
  const ref = useRef(null);
  const [state, setState] = useState("loading");
  useEffect(() => {
    let on = true, pdf = null;
    const box = ref.current;
    box.innerHTML = "";
    setState("loading");
    (async () => {
      try {
        const buf = (await axios.get(src, { responseType: "arraybuffer" })).data;
        pdf = await pdfjsLib.getDocument({ data: buf }).promise;
        for (let i = 1; i <= pdf.numPages && on; i++) {
          const page = await pdf.getPage(i);
          const v0 = page.getViewport({ scale: 1 });
          const w = Math.min(box.clientWidth || 900, 1100);
          const vp = page.getViewport({ scale: (w / v0.width) * (window.devicePixelRatio || 1) });
          const c = document.createElement("canvas");
          c.width = Math.ceil(vp.width); c.height = Math.ceil(vp.height);
          c.style.width = "100%"; c.className = "block bg-white shadow mb-3 rounded";
          await page.render({ canvasContext: c.getContext("2d"), viewport: vp }).promise;
          if (on) { box.appendChild(c); if (i === 1) setState("ok"); }
        }
      } catch { if (on) setState("err"); }
    })();
    return () => { on = false; if (pdf) pdf.destroy(); };
  }, [src]);
  return (
    <div className="w-full max-w-[1100px] mx-auto">
      {state === "loading" && <div className="py-20 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-white" /></div>}
      {state === "err" && <p className="py-20 text-center text-white">Anteprima non disponibile. Usa "Scarica".</p>}
      <div ref={ref} data-testid="share-pub-viewer-pdf" />
    </div>
  );
};

const PubViewer = ({ files, idx, setIdx, fileUrl, onDownload, onClose }) => {
  const a = files[idx];
  useEffect(() => {
    const k = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft" && idx > 0) setIdx(idx - 1);
      if (e.key === "ArrowRight" && idx < files.length - 1) setIdx(idx + 1);
    };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [idx, files.length, setIdx, onClose]);
  const btn = "h-9 w-9 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center disabled:opacity-30";
  return (
    <div className="fixed inset-0 z-[200] bg-black/90 flex flex-col" data-testid="share-pub-viewer">
      <div className="flex items-center gap-2 px-3 py-2 text-white">
        <button type="button" className={btn} disabled={idx === 0} onClick={() => setIdx(idx - 1)} data-testid="share-pub-viewer-prev" aria-label="Precedente"><ChevronLeft className="h-5 w-5" /></button>
        <button type="button" className={btn} disabled={idx >= files.length - 1} onClick={() => setIdx(idx + 1)} data-testid="share-pub-viewer-next" aria-label="Successivo"><ChevronRight className="h-5 w-5" /></button>
        <span className="flex-1 min-w-0 truncate text-sm" data-testid="share-pub-viewer-name">{a.nome} <span className="text-white/50">({idx + 1}/{files.length})</span></span>
        <button type="button" onClick={() => onDownload(a.id)} data-testid="share-pub-viewer-download" className="rounded-full bg-amber-600 hover:bg-amber-700 px-3 h-9 text-sm font-medium flex items-center gap-1"><Download className="h-4 w-4" /> Scarica</button>
        <button type="button" className={btn} onClick={onClose} data-testid="share-pub-viewer-close" aria-label="Chiudi"><X className="h-5 w-5" /></button>
      </div>
      <div className="flex-1 min-h-0 overflow-auto p-3 flex items-start justify-center">
        {isPdf(a) ? <PdfPages src={fileUrl(a)} />
          : <img src={fileUrl(a)} alt={a.nome} className="max-w-full max-h-full object-contain m-auto" data-testid="share-pub-viewer-img" />}
      </div>
    </div>
  );
};

const OtpGate = ({ token, email, onVerified }) => {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [sentTo, setSentTo] = useState(email || "");
  const sent = useRef(false);
  const send = () => axios.post(`${API}/${token}/otp/send`).then(({ data }) => { setSentTo(data.email || email); setMsg(data.wait ? "Codice già inviato: attendi un minuto prima di richiederne un altro." : "Codice inviato."); })
    .catch((e) => setMsg(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Invio del codice non riuscito."));
  useEffect(() => { if (!sent.current) { sent.current = true; send(); } });
  const verify = async () => {
    if (!code.trim()) return;
    setBusy(true); setMsg("");
    try { const { data } = await axios.post(`${API}/${token}/otp/verify`, { code: code.trim() }); onVerified(data.session); }
    catch (e) { setMsg(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Verifica non riuscita."); }
    finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-testid="share-otp-gate">
      <div className="w-full max-w-sm rounded-xl border border-slate-200 bg-white p-7 shadow-sm text-center">
        <ShieldAlert className="h-9 w-9 text-amber-500 mx-auto" />
        <h1 className="mt-3 text-lg font-semibold text-slate-900">Verifica di sicurezza</h1>
        <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5 text-left text-sm text-slate-700" data-testid="share-otp-notice">
          Per accedere ai documenti è necessario verificare la tua identità. Ti abbiamo inviato un codice di verifica all'indirizzo email con cui sei stato contattato{sentTo ? <> (<strong data-testid="share-otp-email">{sentTo}</strong>)</> : ""}. Inserisci il codice per procedere.
        </div>
        <input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} onKeyDown={(e) => { if (e.key === "Enter") verify(); }} inputMode="numeric" autoFocus placeholder="000000" data-testid="share-otp-code"
          className="mt-4 w-full rounded-md border border-slate-300 px-3 py-2 text-center text-2xl tracking-[0.4em] font-mono text-slate-900 focus:outline-none focus:ring-2 focus:ring-amber-400" />
        <button type="button" onClick={verify} disabled={busy || code.length < 6} data-testid="share-otp-verify"
          className="mt-3 w-full inline-flex items-center justify-center gap-1.5 rounded-md bg-amber-500 hover:bg-amber-600 disabled:opacity-50 px-4 py-2 text-sm font-semibold text-slate-900 transition-colors">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} Verifica e apri
        </button>
        <button type="button" onClick={send} data-testid="share-otp-resend" className="mt-3 text-sm text-amber-700 hover:underline">Invia di nuovo il codice</button>
        {msg && <p className="mt-2 text-sm text-slate-600" data-testid="share-otp-msg">{msg}</p>}
      </div>
    </div>
  );
};

export default function CondivisionePublic() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [sel, setSel] = useState([]);
  const [view, setView] = useState(() => localStorage.getItem(VIEW_KEY) || "grid");
  const [askExit, setAskExit] = useState(false);
  const [ended, setEnded] = useState(false);
  const [viewIdx, setViewIdx] = useState(-1);
  const [otp, setOtp] = useState(null);
  const lastSelRef = useRef(null);
  const logout = () => {
    const s = data?.session || "";
    sessionStorage.removeItem(`pgp_share_sess_${token}`);
    axios.post(`${API}/${token}/logout`, null, { params: { s } }).catch(() => {});
    setData(null); setSel([]); setEnded(true);
  };
  useEffect(() => {
    const k = `pgp_share_sess_${token}`;
    const s = sessionStorage.getItem(k) || "";
    axios.get(`${API}/${token}`, { params: s ? { s } : {} })
      .then(({ data: d }) => { sessionStorage.setItem(k, d.session); setData(d); })
      .catch((e) => {
        const det = e.response?.data?.detail;
        if (e.response?.status === 403 && det?.otp_required) { setOtp({ email: det.email }); return; }
        setErr(typeof det === "string" ? det : "Questo link non è più valido o è scaduto. Contatta lo studio per richiedere un nuovo accesso.");
      });
  }, [token]);
  const onOtpVerified = (sess) => {
    sessionStorage.setItem(`pgp_share_sess_${token}`, sess);
    axios.get(`${API}/${token}`, { params: { s: sess, first: 1 } })
      .then(({ data: d }) => { setOtp(null); setData(d); })
      .catch((e) => setErr(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Questo link non è più valido o è scaduto."));
  };
  const groups = useMemo(() => {
    const g = {};
    (data?.allegati || []).forEach((a) => { (g[a.categoria] = g[a.categoria] || []).push(a); });
    return Object.entries(g).sort(([a], [b]) => (CAT_LABEL[a] || a).localeCompare(CAT_LABEL[b] || b));
  }, [data]);
  const setV = (v) => { setView(v); localStorage.setItem(VIEW_KEY, v); };
  const base = `${API}/${token}`;
  const fileUrl = (a, dl) => `${base}/files/${a.id}?s=${encodeURIComponent(data.session)}${dl ? "&download=1" : ""}`;
  const download = (ids) => ids.forEach((id, i) => setTimeout(() => {
    const a = data.allegati.find((x) => x.id === id);
    const el = document.createElement("a"); el.href = fileUrl(a, true); el.download = a.nome; document.body.appendChild(el); el.click(); el.remove();
  }, i * 400));
  const flipSel = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const toggle = (id) => { lastSelRef.current = id; flipSel(id); };
  const allOrder = groups.flatMap(([, list]) => list).map((a) => a.id);
  const modClick = (id) => (e) => modSelect(e, id, allOrder, lastSelRef, flipSel, (ids) => setSel((s) => [...new Set([...s, ...ids])]));
  const viewable = groups.flatMap(([, list]) => list).filter((a) => isImg(a) || isPdf(a));
  const openView = (a) => (e) => { const i = viewable.findIndex((x) => x.id === a.id); if (i < 0) return; e.preventDefault(); setViewIdx(i); };

  if (ended) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-testid="share-ended">
      <div className="max-w-md rounded-xl border border-slate-200 bg-white p-8 text-center shadow-sm">
        <CheckCircle2 className="h-10 w-10 text-emerald-500 mx-auto" />
        <p className="mt-4 text-lg font-medium text-slate-800" data-testid="share-ended-msg">Sessione terminata. Grazie.</p>
      </div>
    </div>
  );
  if (err) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-testid="share-expired">
      <div className="max-w-md rounded-xl border border-slate-200 bg-white p-8 text-center shadow-sm">
        <ShieldAlert className="h-10 w-10 text-amber-500 mx-auto" />
        <p className="mt-4 text-slate-700" data-testid="share-expired-msg">{err}</p>
      </div>
    </div>
  );
  if (otp) return <OtpGate token={token} email={otp.email} onVerified={onOtpVerified} />;
  if (!data) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="h-6 w-6 animate-spin text-amber-500" /></div>;
  const { studio, pratica, destinatario } = data;
  return (
    <div className="min-h-screen bg-slate-50 text-slate-800" data-testid="share-public-page">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-5 py-5 flex items-start sm:items-center gap-4">
          {studio.logo && <img src={studio.logo} alt="" className="h-14 w-14 object-contain" data-testid="share-studio-logo" />}
          <div className="min-w-0 break-words">
            <div className="font-semibold text-lg text-slate-900" data-testid="share-studio-name">{studio.nome}</div>
            <div className="text-sm text-slate-500">{[studio.indirizzo, studio.email, studio.telefono].filter(Boolean).join(" · ")}</div>
          </div>
          <button type="button" onClick={() => setAskExit(true)} data-testid="share-pub-logout" className="ml-auto shrink-0 inline-flex items-center gap-1.5 rounded-md bg-red-600 hover:bg-red-700 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors"><LogOut className="h-4 w-4" /> Esci</button>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-5 py-6 space-y-5">
        <section className="rounded-xl border border-slate-200 bg-white p-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm" data-testid="share-intestazione">
          <div><div className="text-xs uppercase text-slate-500">Compagnia assicurativa</div><div className="font-medium">{pratica.compagnia || "—"}</div></div>
          <div><div className="text-xs uppercase text-slate-500">Numero sinistro</div><div className="font-medium font-mono">{pratica.num_sinistro || "—"}</div></div>
          <div><div className="text-xs uppercase text-slate-500">Pratica studio</div><div className="font-medium font-mono">{pratica.numero || "—"}</div></div>
          <div><div className="text-xs uppercase text-slate-500">Cliente assistito</div><div className="font-medium">{pratica.cliente || "—"}{pratica.cliente_cf ? ` · ${pratica.cliente_cf}` : ""}</div></div>
          <div><div className="text-xs uppercase text-slate-500">Destinatario</div><div className="font-medium">{destinatario.nome}{destinatario.ruolo ? ` — ${destinatario.ruolo}` : ""}</div></div>
          <div><div className="text-xs uppercase text-slate-500">Accesso valido fino al</div><div className="font-medium">{data.expires_at ? new Date(data.expires_at).toLocaleString("it-IT") : "—"}</div></div>
        </section>
        <ShareMessages token={token} data={data} setData={setData} />
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => setSel(sel.length === data.allegati.length ? [] : data.allegati.map((a) => a.id))} data-testid="share-pub-select-all" className="text-sm text-amber-700 hover:underline">{sel.length === data.allegati.length ? "Deseleziona tutti" : "Seleziona tutti"}</button>
            <button type="button" disabled={!sel.length} onClick={() => download(sel)} data-testid="share-pub-download" className="inline-flex items-center gap-1.5 rounded-md bg-amber-500 hover:bg-amber-600 disabled:opacity-50 px-3 py-1.5 text-sm font-semibold text-slate-900"><Download className="h-4 w-4" /> Scarica selezionati ({sel.length})</button>
          </div>
          <div className="flex rounded-md border border-slate-200 bg-white overflow-hidden">
            <button type="button" onClick={() => setV("grid")} data-testid="share-view-grid" className={`px-2.5 py-1.5 ${view === "grid" ? "bg-amber-100 text-amber-700" : "text-slate-500"}`}><LayoutGrid className="h-4 w-4" /></button>
            <button type="button" onClick={() => setV("list")} data-testid="share-view-list" className={`px-2.5 py-1.5 ${view === "list" ? "bg-amber-100 text-amber-700" : "text-slate-500"}`}><List className="h-4 w-4" /></button>
          </div>
        </div>
        {groups.map(([cat, list]) => (
          <section key={cat}>
            <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">{CAT_LABEL[cat] || cat}</h2>
            {view === "grid" ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                {list.map((a) => (
                  <div key={a.id} className={`rounded-lg border bg-white p-2 ${sel.includes(a.id) ? "border-amber-500 ring-1 ring-amber-400" : "border-slate-200"}`} data-testid={`share-pub-file-${a.id}`} onClickCapture={modClick(a.id)} onMouseDown={noShiftTextSelect}>
                    <a href={fileUrl(a)} target="_blank" rel="noreferrer" onClick={openView(a)} data-testid={`share-pub-open-${a.id}`}><Thumb base={base} a={a} big s={data.session} src={fileUrl(a)} /></a>
                    <div className="mt-2 flex items-start gap-2"><Checkbox checked={sel.includes(a.id)} onCheckedChange={() => toggle(a.id)} data-testid={`share-pub-check-${a.id}`} /><a href={fileUrl(a)} target="_blank" rel="noreferrer" onClick={openView(a)} className="text-xs break-all line-clamp-2 hover:text-amber-700" data-testid={`share-pub-name-${a.id}`}>{a.nome}</a></div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="rounded-lg border border-slate-200 bg-white divide-y divide-slate-100">
                {list.map((a) => (
                  <div key={a.id} className="flex items-center gap-3 px-3 py-2" data-testid={`share-pub-row-${a.id}`} onClickCapture={modClick(a.id)} onMouseDown={noShiftTextSelect}>
                    <Checkbox checked={sel.includes(a.id)} onCheckedChange={() => toggle(a.id)} data-testid={`share-pub-rcheck-${a.id}`} />
                    <Thumb base={base} a={a} s={data.session} src={fileUrl(a)} />
                    <a href={fileUrl(a)} target="_blank" rel="noreferrer" onClick={openView(a)} className="flex-1 text-sm break-all hover:text-amber-700" data-testid={`share-pub-rname-${a.id}`}>{a.nome}</a>
                    <button type="button" onClick={() => download([a.id])} data-testid={`share-pub-dl-${a.id}`} className="text-slate-500 hover:text-amber-700"><Download className="h-4 w-4" /></button>
                  </div>
                ))}
              </div>
            )}
          </section>
        ))}
      </main>
      {viewIdx >= 0 && viewable[viewIdx] && (
        <PubViewer files={viewable} idx={viewIdx} setIdx={setViewIdx} fileUrl={fileUrl} onDownload={(id) => download([id])} onClose={() => setViewIdx(-1)} />
      )}
      <AlertDialog open={askExit} onOpenChange={setAskExit}>
        <AlertDialogContent data-testid="share-logout-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Vuoi uscire?</AlertDialogTitle>
            <AlertDialogDescription>La sessione di consultazione degli allegati verrà chiusa.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="share-logout-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={logout} data-testid="share-logout-confirm" className="bg-red-600 hover:bg-red-700">Esci</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
